package fr.manyverse.app.infrastructure.repository

import fr.manyverse.app.domain.model.UserConfig
import fr.manyverse.app.domain.repository.UserConfigRepository
import fr.manyverse.app.infrastructure.error.mapToDomainException
import io.github.jan.supabase.SupabaseClient
import io.github.jan.supabase.postgrest.from
import io.github.jan.supabase.postgrest.query.Columns
import kotlinx.coroutines.CancellationException
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlin.time.Clock
import kotlin.time.Instant

class SupabaseUserConfigRepository(
    private val supabaseClient: SupabaseClient
) : UserConfigRepository {

    companion object {
        private const val TABLE = "user_config"
    }

    override suspend fun getUserConfig(userId: String): Result<UserConfig?> {
        return try {
            val dto = supabaseClient
                .from(TABLE)
                .select(Columns.ALL) {
                    filter { eq("user_id", userId) }
                }
                .decodeSingleOrNull<UserConfigDto>()
            Result.success(dto?.toDomain())
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "userconfig"))
        }
    }

    override suspend fun upsertTermsAcceptance(userId: String): Result<Unit> {
        return try {
            // L'acceptation n'est enregistrée qu'avec une date : la base la remplace par son heure
            val dto = UserConfigUpsertDto(
                userId = userId,
                termsAcceptedAt = Clock.System.now()
            )
            supabaseClient
                .from(TABLE)
                .upsert(dto) {
                    onConflict = "user_id"
                }
            Result.success(Unit)
        } catch (e: CancellationException) {
            throw e
        } catch (e: Exception) {
            Result.failure(mapToDomainException(e, "userconfig"))
        }
    }

    @Serializable
    private data class UserConfigDto(
        @SerialName("user_id") val userId: String,
        @SerialName("terms_accepted_at") val termsAcceptedAt: Instant?
    ) {
        fun toDomain() = UserConfig(userId = userId, termsAcceptedAt = termsAcceptedAt)
    }

    @Serializable
    private data class UserConfigUpsertDto(
        @SerialName("user_id") val userId: String,
        @SerialName("terms_accepted_at") val termsAcceptedAt: Instant
    )
}
